import React, { useCallback, useEffect, useState } from 'react';
import { Empty, Input, Pagination, Spin, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { fetchAdminPaydOrders } from '../../../services';
import AdminOrdersCard from '../AdminOrdersCard';
import './AdminOrders.scss';

const { Title, Text } = Typography;

const AdminOrders: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.user);
  const { orders, status, error, total } = useAppSelector((state) => state.adminPaydOrders);
  const navigate = useNavigate();
  const [openShippingIds, setOpenShippingIds] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const fetchOrders = useCallback(async (nextPage: number, nextSearch: string) => {
    const action = await dispatch(fetchAdminPaydOrders({
      take: pageSize,
      skip: (nextPage - 1) * pageSize,
      search: nextSearch,
    }));

    if (fetchAdminPaydOrders.rejected.match(action)) {
      message.error(action.payload || 'Unable to load orders');
    }
  }, [dispatch]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (!user.isAdmin) {
      navigate('/UserPage');
      return;
    }

    void fetchOrders(1, '');
  }, [user, navigate, fetchOrders]);

  const handleSearch = (value = search) => {
    const nextSearch = value.trim();

    setActiveSearch(nextSearch);
    setPage(1);
    void fetchOrders(1, nextSearch);
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    void fetchOrders(nextPage, activeSearch);
  };

  const toggleShipping = (orderId: string) => {
    setOpenShippingIds((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const handleCopyOrderId = async (orderId: string) => {
    try {
      await navigator.clipboard.writeText(orderId);
      message.success('Order ID copied');
    } catch {
      message.error('Unable to copy Order ID');
    }
  };

  if (!user?.isAdmin) {
    return (
      <div className="admin-orders">
        <Title level={3}>Access denied</Title>
        <Text>This section is reserved for administrators.</Text>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className="admin-orders admin-orders--loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="admin-orders">
      <div className="admin-orders__header">
        <div><Title level={3}>Admin</Title><Text type="secondary">Order management · all paid orders in the system</Text></div>
        <Input.Search
          allowClear
          placeholder="Search order, customer, status"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            if (!event.target.value) {
              setActiveSearch('');
              setPage(1);
              void fetchOrders(1, '');
            }
          }}
          onSearch={handleSearch}
        />
      </div>
      {error ? (
        <Text type="danger">{error}</Text>
      ) : orders.length === 0 ? (
        <Empty description="No orders yet" />
      ) : (
        <div className="admin-orders__list">
          {orders.map((order) => (
            <AdminOrdersCard
              key={order.id}
              order={order}
              isShippingOpen={Boolean(openShippingIds[order.id])}
              onCopyOrderId={handleCopyOrderId}
              onToggleShipping={toggleShipping}
            />
          ))}
          {total > pageSize && (
            <div className="admin-orders__pagination">
              <Pagination
                current={page}
                pageSize={pageSize}
                total={total}
                showSizeChanger={false}
                onChange={handlePageChange}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
